import { SupportedRegions } from "@nova/constants"
import { FileManagement, FileManagementProps } from "@nova/file-management"
import { IngestResponse } from "@nova/schemas"
import EventEmitter from "events"
import { useEffect } from "react"
import { addExceededUsage } from "../workspace/subscriptions/module"
import { isLocalhostEnvironment } from "@nova/configuration/browser"

export const ingestEventEmitter = new EventEmitter()

const FIVE_MINUTES_MS = 5 * 60 * 1000
const THIRTY_SECONDS_MS = 30 * 1000

class NextJSFileManagement extends FileManagement {
    private activeUploads = 0
    private uploadQueue: { resolve: () => void; maxQueue: number }[] = []

    private drainQueue() {
        this.activeUploads--
        const next = this.uploadQueue[0]
        if (next && this.activeUploads <= next.maxQueue) {
            this.uploadQueue.shift()
            next.resolve()
        }
    }

    useCredentialManager = ({ userId, orgId, projectId }: Partial<FileManagementProps>) => {
        // oxlint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            if (!userId || !orgId) return this.cleanup()
            let region = SupportedRegions.Canada
            if ((self as Window & typeof globalThis & { region: SupportedRegions }).region)
                region = (self as Window & typeof globalThis & { region: SupportedRegions }).region
            else {
                try {
                    const defaultRegion = JSON.stringify(SupportedRegions.Canada)
                    region = JSON.parse(sessionStorage.getItem("region") || defaultRegion)
                } catch {}
            }
            this.initialize({ userId, orgId, region, projectId })
        }, [orgId, projectId, userId])
    }

    async putFile(
        body: Blob | Uint8Array,
        options: Parameters<FileManagement["putFile"]>[1] & {
            maxQueue?: number
            // Invoked when ingest does not succeed — either the server reports a
            // failure status or no ack arrives within the timeout. Lets the caller
            // mark its own upload entry errored so a single stuck image doesn't
            // wedge the whole set.
            onIngestError?: (ingest: IngestResponse) => void
        }
    ) {
        const { maxQueue = 6 } = options
        if (this.activeUploads > maxQueue) {
            await new Promise<void>((resolve) => {
                this.uploadQueue.push({ resolve, maxQueue })
            })
        }

        this.activeUploads++
        let response

        try {
            response = await super.putFile(body, options)
        } finally {
            this.drainQueue()
        }

        const requestId = response.ETag
        if (!requestId) throw new Error("No request ID on putFile response")

        const ingestResponse = await new Promise<IngestResponse>((res, rej) => {
            const timeout = setTimeout(
                () => {
                    options.onIngestError?.({ status: "timeout" })
                    rej("timeout")
                },
                isLocalhostEnvironment() ? FIVE_MINUTES_MS : THIRTY_SECONDS_MS
            )
            ingestEventEmitter.once(requestId, (response: IngestResponse) => {
                clearTimeout(timeout)
                if (response.status === "success") res(response)
                else {
                    switch (response.status) {
                        case "outOfSpace": {
                            addExceededUsage(response.flightId, response.size)
                        }
                        default: {
                            options.onIngestError?.(response)
                            rej(response)
                        }
                    }
                }
            })
        }).catch((err: IngestResponse) => err)

        return { ...response, ingestResponse }
    }
}

export const fileManagementApi = new NextJSFileManagement()

export const fetchAsDataUrl = async (url: string) => {
    const response = await fetch(url, { credentials: "include" })
    if (!response.ok) throw new Error(`Fetch failed (${response.status}): ${url}`)
    const blob = await response.blob()
    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result))
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(blob)
    })
}
